import React, { useMemo } from 'react';
import {
  Button,
  Card,
  Empty,
  Image,
  Input,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  SearchOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { mekakApi, formatCost, formatTokens, formatDate, truncate } from './services';
import type { AdvancedSearchResponse, QueryRecord } from './models';
import { useState, useEffect, useCallback } from 'react';
import AnswerCard from './components/AnswerCard';
import LLMSourcesPanel from './components/LLMSourcesPanel';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

// ─── History table columns ────────────────────────────────────────────────────

const historyColumns: ColumnsType<QueryRecord> = [
  {
    title: '查詢者',
    dataIndex: 'username',
    key: 'username',
    width: '12%',
    render: (val: string) => (
      <Text className="text-sm">{val || '-'}</Text>
    ),
  },
  {
    title: '查詢內容',
    dataIndex: 'query',
    key: 'query',
    width: '30%',
    ellipsis: true,
    render: (text: string, record: QueryRecord) => (
      <div>
        <Text strong className="text-sm">{truncate(text, 60)}</Text>
        {record.related_images && record.related_images.length > 0 && (
          <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>
            {record.related_images.length} 圖
          </Tag>
        )}
      </div>
    ),
  },
  {
    title: 'Token',
    dataIndex: 'total_tokens',
    key: 'total_tokens',
    width: '10%',
    align: 'right',
    render: (val: number) => (
      <Text className="text-xs font-mono">{formatTokens(val)}</Text>
    ),
  },
  {
    title: '費用 (USD)',
    dataIndex: 'estimated_cost',
    key: 'estimated_cost',
    width: '13%',
    align: 'right',
    render: (val: number) => (
      <Text className="text-xs font-mono text-gray-500">{formatCost(val)}</Text>
    ),
  },
  {
    title: '時間',
    dataIndex: 'created_at',
    key: 'created_at',
    width: '15%',
    render: (val: string) => (
      <Text type="secondary" className="text-xs">{formatDate(val)}</Text>
    ),
  },
];

// ─── Main component ───────────────────────────────────────────────────────────

const AISearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<AdvancedSearchResponse | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [enhanceQuery, setEnhanceQuery] = useState(false);

  const [history, setHistory] = useState<QueryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await mekakApi.getHistory();
      if (res.data.success && Array.isArray(res.data.data)) {
        setHistory(res.data.data);
      }
    } catch {
      // non-critical
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) {
      message.warning('請輸入查詢問題後再搜尋');
      return;
    }

    setSearching(true);
    setResult(null);
    setHasSearched(true);

    try {
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      let userId = 'anonymous';
      if (userStr) {
        try { userId = JSON.parse(userStr).userId || 'anonymous'; } catch { /* ignore */ }
      }
      const res = await mekakApi.search({ query: trimmed, enhance_query: enhanceQuery, user_id: userId });
      if (res.data.success && res.data.data) {
        setResult(res.data.data);
        fetchHistory();
      } else {
        message.error('搜尋失敗，請稍後再試');
      }
    } catch (err: unknown) {
      const errMsg =
        err instanceof Error ? err.message : '網路錯誤，請確認後端服務是否正常';
      message.error(`搜尋錯誤：${errMsg}`);
    } finally {
      setSearching(false);
    }
  }, [query, enhanceQuery, fetchHistory]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div>
      <div className="max-w-5xl mx-auto">
        <Card className="mb-6 shadow-sm">
          <div className="flex flex-col gap-3">
            <TextArea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="輸入語意問題，例如：什麼是出貨前付款的條件？跟運費相關的政策有哪些？"
              autoSize={{ minRows: 3, maxRows: 8 }}
              disabled={searching}
              style={{ fontSize: '15px', resize: 'none' }}
              aria-label="知識庫搜尋輸入框"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Text type="secondary" className="text-xs">
                  按 Ctrl+Enter 快速搜尋
                </Text>
                <Tooltip title="啟用後會先用 AI 擴展查詢語意（同義詞、英文對照等），搜尋較慢但可能找到更多結果">
                  <div className="flex items-center gap-1.5">
                    <Switch
                      size="small"
                      checked={enhanceQuery}
                      onChange={setEnhanceQuery}
                    />
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <ThunderboltOutlined />
                      擴大語意查詢
                    </span>
                  </div>
                </Tooltip>
              </div>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                loading={searching}
                onClick={handleSearch}
                disabled={!query.trim()}
                style={{ minWidth: '120px' }}
              >
                {searching ? '搜尋中...' : '開始搜尋'}
              </Button>
            </div>
          </div>
        </Card>

        <Spin spinning={searching} tip="正在搜尋知識庫..." size="large">
          {hasSearched && !searching && (
            <div className="mb-8">
              {result ? (
                <>
                  <AnswerCard result={result} />
                  <LLMSourcesPanel llmAnswers={result.llm_answers} />
                </>
              ) : (
                <Card className="shadow-sm">
                  <Empty
                    description={
                      <span className="text-gray-500">
                        找不到相關資料，請嘗試換個問法或確認知識庫是否有相關內容
                      </span>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </Card>
              )}
            </div>
          )}
        </Spin>

        <Card
          className="shadow-sm"
          title={
            <span className="flex items-center gap-2 text-gray-700">
              <ClockCircleOutlined />
              近期查詢記錄
            </span>
          }
          extra={
            <Button
              type="text"
              size="small"
              onClick={fetchHistory}
              loading={historyLoading}
            >
              重新整理
            </Button>
          }
        >
          <Spin spinning={historyLoading}>
            {history.length > 0 ? (
              <Table<QueryRecord>
                dataSource={history}
                columns={historyColumns}
                rowKey="id"
                size="small"
                expandable={{
                  expandedRowRender: (record) => (
                    <div className="space-y-3 py-2">
                      <div>
                        <Text type="secondary" className="text-xs block mb-1">查詢問題</Text>
                        <Paragraph className="mb-0 text-sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {record.query}
                        </Paragraph>
                      </div>
                      {record.answer && (
                        <div>
                          <Text type="secondary" className="text-xs block mb-1">AI 回答</Text>
                          <Paragraph className="mb-0 text-sm" style={{ whiteSpace: 'pre-wrap', color: '#1f2937' }}>
                            {record.answer}
                          </Paragraph>
                        </div>
                      )}
                      {record.related_images && record.related_images.length > 0 && (
                        <div>
                          <Text type="secondary" className="text-xs block mb-1">相關圖片</Text>
                          <Image.PreviewGroup>
                            <div className="flex gap-2 flex-wrap">
                              {record.related_images.map((img) => (
                                <Image
                                  key={img.id}
                                  src={img.gcs_url}
                                  alt={img.ocr_text || `Page ${img.page_number}`}
                                  width={120}
                                  height={80}
                                  style={{ objectFit: 'contain', borderRadius: 4, border: '1px solid #f0f0f0' }}
                                />
                              ))}
                            </div>
                          </Image.PreviewGroup>
                        </div>
                      )}
                    </div>
                  ),
                  rowExpandable: () => true,
                }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showTotal: (total) => `共 ${total} 筆記錄`,
                  size: 'small',
                }}
                scroll={{ x: 700 }}
                locale={{
                  emptyText: (
                    <Empty
                      description="尚無查詢記錄"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
              />
            ) : (
              !historyLoading && (
                <Empty
                  description="尚無查詢記錄，試試上方的搜尋功能"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default AISearch;
